import React from "react";
import Badge from "../../../components/common/Badge";
import { formatNumber } from "../../../services/analyticsService";
import {
  UserGroupIcon,
  DeviceMobileIcon,
  DocumentTextIcon,
  CalendarIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const CampaignReviewStep = ({ wizardDraft, template }) => {
  const { audience, content, schedule } = wizardDraft;

  return (
    <div className="space-y-6">
      {/* Summary Alert */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Review Your Campaign
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Please review all details before sending. This campaign will reach
              approximately {formatNumber(audience.estimatedReach || 0)} users.
            </p>
          </div>
        </div>
      </div>

      {/* Audience Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserGroupIcon className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold">Audience</h4>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-500">Segments</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {audience.segments.length > 0 ? (
                audience.segments.map((segment) => (
                  <Badge key={segment} variant="info">
                    {segment}
                  </Badge>
                ))
              ) : (
                <span className="text-gray-600">All users</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500">Platforms</p>
            <div className="flex gap-2 mt-1">
              {audience.platforms.map((platform) => (
                <Badge key={platform} variant="secondary">
                  {platform}
                </Badge>
              ))}
            </div>
          </div>
          {audience.testDevices.length > 0 && (
            <div>
              <p className="text-sm text-gray-500">Test Devices</p>
              <p className="text-sm mt-1">{audience.testDevices.length} devices</p>
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <DocumentTextIcon className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold">Content</h4>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-500">Template</p>
            <p className="font-medium">
              {template?.name || content.templateKey} (v{content.templateVersion})
            </p>
          </div>
          {Object.keys(content.variables).length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Variables</p>
              <div className="bg-white dark:bg-gray-800 rounded p-3 space-y-1">
                {Object.entries(content.variables).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <code className="text-primary">{key}</code>
                    <span className="text-gray-600">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold">Schedule</h4>
        </div>
        <div>
          {schedule.sendNow ? (
            <p className="text-green-600 dark:text-green-400 font-medium">
              Send immediately
            </p>
          ) : (
            <div>
              <p className="text-sm text-gray-500">Scheduled for</p>
              <p className="font-medium">
                {new Date(schedule.scheduledTime).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignReviewStep;
